<template>
  <div class="result-summary">
    <!-- Stats row -->
    <v-row class="mb-4">
      <v-col cols="6" sm="3">
        <div class="stat-box">
          <div class="stat-value">{{ result.metadata?.processed_frames ?? '—' }}</div>
          <div class="stat-label">Frames analysed</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="stat-box">
          <div class="stat-value text-success">{{ result.rolls?.length ?? 0 }}</div>
          <div class="stat-label">Rolls detected</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="stat-box">
          <div class="stat-value">{{ result.summary?.crops_saved ?? 0 }}</div>
          <div class="stat-label">Crops saved</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="stat-box">
          <div class="stat-value">{{ result.metadata?.elapsed_sec ?? '—' }}s</div>
          <div class="stat-label">Processing time</div>
        </div>
      </v-col>
    </v-row>

    <!-- Rolls table -->
    <div v-if="result.rolls?.length" class="mb-4">
      <p class="text-caption font-weight-bold text-medium-emphasis mb-2">DETECTED ROLLS</p>
      <v-table density="compact">
        <thead>
          <tr>
            <th>#</th>
            <th>Timestamp</th>
            <th>Frame</th>
            <th>Dice found</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(roll, i) in result.rolls" :key="i">
            <td class="text-caption text-medium-emphasis">{{ i + 1 }}</td>
            <td class="text-caption">{{ formatTime(roll.timestamp_sec) }}</td>
            <td class="text-caption text-medium-emphasis">{{ roll.frame_idx }}</td>
            <td>
              <v-chip
                v-for="n in roll.dice_count"
                :key="n"
                size="x-small"
                color="primary"
                variant="tonal"
                class="mr-1"
              >
                🎲
              </v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <div v-else class="text-center text-disabled py-4">
      <v-icon size="32" class="mb-1">mdi-dice-multiple-outline</v-icon>
      <p class="text-caption">No rolls detected</p>
    </div>

    <!-- Action buttons -->
    <div class="d-flex gap-2 flex-wrap">
      <v-btn
        size="small"
        variant="outlined"
        prepend-icon="mdi-download"
        @click="downloadJson"
      >
        Download JSON
      </v-btn>
      <v-btn
        v-if="jobId && (result.summary?.crops_saved ?? 0) > 0"
        size="small"
        variant="flat"
        color="primary"
        prepend-icon="mdi-dice-multiple"
        :to="`/admin/analysis/${jobId}/review`"
      >
        Review Pip Values ({{ result.summary?.crops_saved ?? 0 }})
      </v-btn>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  result: { type: Object, required: true },
  jobId:  { type: String, default: null },
})

function formatTime(sec) {
  if (sec == null) return '—'
  const m = Math.floor(sec / 60)
  const s = (sec % 60).toFixed(1).padStart(4, '0')
  return `${m}:${s}`
}

function downloadJson() {
  const blob = new Blob([JSON.stringify(props.result, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `analysis_${props.jobId || Date.now()}.json`
  a.click()
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
.stat-box {
  text-align: center;
  padding: 12px;
  background: rgba(var(--v-theme-surface-variant), 0.4);
  border-radius: 8px;
}
.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}
.stat-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}
</style>